/** Only fixed operation labels may reach the opt-in console; never log URL/body. */
function requestOperation(path: string, method: string): string | null {
  let operation =
    method === 'POST' &&
    /^\/quote-masters\/\d+\/versions\/\d+:save-draft$/.test(path)
      ? 'quote.save-draft'
      : method === 'GET' && /^\/quote-masters\/\d+(?:\?|$)/.test(path)
        ? 'quote.read'
        : method === 'GET' && /^\/customers(?:\?|$)/.test(path)
          ? 'customer.search'
          : null;
  if (!operation) {
    const catalogRead =
      method === 'GET'
        ? /^\/(equipment|packages|plans)(?:\/[1-9][0-9]{0,18})?(?:\?[^#]*)?$/.exec(
            path,
          )
        : null;
    const catalogCreate =
      method === 'POST' ? /^\/(equipment|packages|plans)$/.exec(path) : null;
    const packageRevision =
      method === 'POST' &&
      /^\/packages\/[1-9][0-9]{0,18}\/revisions$/.test(path);
    operation = catalogRead
      ? `${catalogRead[1]}.read`
      : catalogCreate
        ? `${catalogCreate[1]}.create`
        : packageRevision
          ? 'packages.update'
          : method === 'PATCH' && /^\/equipment\/[1-9][0-9]{0,18}$/.test(path)
            ? 'equipment.update'
            : method === 'POST' && path === '/equipment:batch-get'
              ? 'equipment.batch-read'
              : method === 'POST' && path === '/packages:batch-get'
                ? 'packages.batch-read'
                : method === 'POST' &&
                    path === '/plan-package-memberships:replace'
                  ? 'plans.update'
                  : null;
  }
  if (!operation && method === 'POST') {
    const quoteCommand =
      /^\/quote-masters\/\d+\/versions\/\d+:(check-sources|prepare-image|verify-image|preview-output)$/.exec(
        path,
      );
    operation = quoteCommand
      ? `quote.${quoteCommand[1]}`
      : /^\/quote-masters\/\d+\/customer-link:publish$/.test(path)
        ? 'quote.publish'
        : path === '/auth/refresh'
          ? 'session.refresh'
          : path === '/auth/eligibility/preflight'
            ? 'session.eligibility'
            : null;
  }
  if (!operation && method === 'GET' && path === '/me')
    operation = 'session.me';
  if (
    !operation &&
    method === 'GET' &&
    /^\/quote-masters\/\d+\/read-state(?:\?[^#]*)?$/.test(path)
  )
    operation = 'quote.read-state';
  return operation;
}

type ClientPhase =
  | 'flight-before'
  | 'flight-after'
  | 'credential-before'
  | 'credential-after'
  | 'refresh'
  | 'rotation-lock'
  | 'quantity'
  | 'equipment'
  | 'content'
  | 'request'
  | 'body'
  | 'snapshot';

function startPhase(operation: string | null, phase: ClientPhase) {
  const started =
    operation && import.meta.env.VITE_CPQ_PERFORMANCE_ENABLED === 'true'
      ? performance.now()
      : null;
  let ended = false;
  return () => {
    if (started === null || ended) return;
    ended = true;
    try {
      console.info(
        '[cpq-client-performance]',
        JSON.stringify({
          operation,
          phase,
          durationMs: Math.round((performance.now() - started) * 100) / 100,
        }),
      );
    } catch {
      // Timing must never change an acknowledged business result.
    }
  };
}

/** Fixed labels only: never include a URL, payload, credential or customer. */
export function startClientPerformancePhase(
  operation: 'session' | 'quote.sources' | 'quote.readiness',
  phase: ClientPhase,
) {
  return startPhase(operation, phase);
}

export function startRequestBodyPerformance(path: string, method: string) {
  return startPhase(requestOperation(path, method.toUpperCase()), 'body');
}

export function recordRequestPerformance(
  path: string,
  method: string,
  startedAt: number,
  response: Response,
) {
  const operation = requestOperation(path, method);
  if (!operation) return;
  const timing = response.headers
    .get('server-timing')
    ?.match(/(?:^|,)\s*app;dur=([\d.]+)/);
  const traceId = response.headers.get('x-trace-id');
  console.info(
    '[cpq-request-performance]',
    JSON.stringify({
      operation,
      durationToHeadersMs:
        Math.round((performance.now() - startedAt) * 100) / 100,
      status: response.status,
      ...(timing && Number.isFinite(Number(timing[1]))
        ? { appDurationMs: Number(timing[1]) }
        : {}),
      ...(traceId &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        traceId,
      )
        ? { traceId }
        : {}),
    }),
  );
}
